import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import {
  Button, TextInput, Select, Group, Stack, Text,
  NumberInput,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import Modal from '@/components/Modal';
import { getMutationResult } from '@/helpers/query';
import {
  useProblemConductMutation,
  useSendingToTheCustomerForSignatureMutation,
} from '@/services/api';
import { BANKS } from '@/data/constants/banks';
import { routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import validate from '@/helpers/validations';
import { PROBLEM_TYPES } from '@/helpers/enums';

const SendDetailsModal = ({ opened, onClose, data = {} }) => {
  const { bankingConduct, type = PROBLEM_TYPES.any } = data;
  const { t } = useTranslation();
  const borrowersStore = useSelector((state) => state.proposal.borrower);
  const proposal = useSelector((state) => state.proposal);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [loading, setLoading] = useState(false);
  const { extraDetails, id } = proposal;
  const navigate = useNavigate();
  const { role } = useSelector((state) => state.user)

  const [apiContact] = useProblemConductMutation();
  const [apiConfigSendToClientForSignature] = useSendingToTheCustomerForSignatureMutation();

  const getBankName = (bankValue) => {
    const bank = BANKS.find((b) => b.value === bankValue);
    return bank ? bank.name : bankValue;
  };

  const isRecycling = extraDetails?.isRecycling === 'yes';

  const fieldValidations = {
    fullName: (value) => validate(value, {
      required: t('globals.validations.required'),
    }),
    phoneNumber: (value) => validate(value, {
      required: t('globals.validations.required'),
      numbers: t('globals.validations.number'),
    }),
    email: (value) => validate(value, {
      required: t('globals.validations.required'),
      email: t('globals.validations.email'),
    }),
    bank: (value) => isRecycling && validate(value, {
      required: t('globals.validations.required'),
    }),
    currentLoanAmount: (value) => isRecycling && validate(value, {
      required: t('globals.validations.required'),
      numbers: t('globals.validations.number'),
    }),
    loanAmount: (value) => validate(value, {
      required: t('globals.validations.required'),
      numbers: t('globals.validations.number'),
    }),
    loanType: (value) => validate(value, {
      required: t('globals.validations.required'),
    }),

  };

  const form = useForm({
    initialValues: {
      bank: '',
      currentLoanAmount: '',
      loanAmount: '',
      loanType: '',
      phoneNumber: '',
      fullName: '',
      email: '',
      message: '',
    },
    validate: fieldValidations,
  });

  useEffect(() => {
    if (!opened) return;
    const firstBorrower = borrowersStore?.[0] || {};
    form.setValues({
      bank: extraDetails?.bank || '',
      currentLoanAmount: extraDetails?.currentLoanAmount || '',
      loanAmount: extraDetails?.loanAmount || '',
      loanType: extraDetails?.loanTypeNameHebrew || '',
      phoneNumber: firstBorrower?.phoneNumber || '',
      email: firstBorrower?.email || '',
      fullName: firstBorrower?.fullName || '',
    });
  }, [opened]);

  const bankingConductData = {
    returnedChecks: {
      label: "החזרת צ'קים",
      value: bankingConduct?.returnedChecks === 'yes' ? 'כן' : 'לא',
    },
    standingOrderReturn: {
      label: 'החזרת הוראת קבע',
      value: bankingConduct?.standingOrderReturn === 'yes' ? 'כן' : 'לא',
    },
    bankAccountRestriction: {
      label: 'הגבלת חשבון בנק',
      value: bankingConduct?.bankAccountRestriction === 'yes' ? 'כן' : 'לא',
    },
    enforcementFile: {
      label: 'תיק בהוצאה לפועל',
      value: bankingConduct?.enforcementFile === 'yes' ? 'כן' : 'לא',
    },
  };

  const handleSendData = async () => {
    const sendValues = {
      offerId: id,
      bank: getBankName(form.values.bank),
      currentLoanAmount: form.values.currentLoanAmount,
      loanAmount: form.values.loanAmount,
      email: form.values.email,
      loanType: form.values.loanType,
      phoneNumber: form.values.phoneNumber,
      fullName: form.values.fullName,
      bankingConduct: bankingConductData,
      type,
      message: form.values.message,
    };

    setLoading(true);
    try {
      const { isError } = await getMutationResult(sendValues, apiContact);
      let sendToClientError = false;

      if (role?.code !== '5') {
        const sendSummaryLinkObj = {
          phoneNumber: form.values.phoneNumber,
          fullName: form.values.fullName,
          email: form.values.email,
          proposalNumber: id,
          problem: true,
        };

        const { isError: clientError } = await getMutationResult(
          sendSummaryLinkObj,
          apiConfigSendToClientForSignature,
        );
        sendToClientError = clientError;
      }

      if (isError || sendToClientError) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      } else {
        closeModal();
        const successPage = routes.newProposal.pages.find((page) => page.id === 'success');
        navigate(routeWithParams(successPage.path, { id, isReferral: true }));
      }
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      close={onClose}
      title={t('pages.existingMortgageSummary.modal.title')}
      footer={
        <Group justify="end" mt="md">
          <Button
            loading={loading}
            leftSection={<IconCheck size={18} />}
            onClick={form.onSubmit(handleSendData)}>
            {t('pages.businessCredit.transferToConsultant')}
          </Button>
        </Group>
      }
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed" mb="md">
          {t('pages.existingMortgageSummary.modal.confirmationMessage')}
        </Text>

        <Group grow>
          {isRecycling && <Select
            label={t('pages.existingMortgageSummary.modal.bank')}
            data={BANKS.map((bank) => ({ value: bank.value, label: bank.name }))}
            searchable
            clearable
            required
            {...form.getInputProps('bank')}
          />
          }
          {isRecycling && <NumberInput
            label={t('pages.existingMortgageSummary.modal.currentLoanAmount')}
            required
            {...form.getInputProps('currentLoanAmount')}
          />}

          <NumberInput
            label={t('pages.existingMortgageSummary.modal.loanAmount')}
            required
            {...form.getInputProps('loanAmount')}
          />
        </Group>

        <TextInput
          label={t('pages.existingMortgageSummary.modal.loanTypeNameHebrew')}
          {...form.getInputProps('loanType')}
          required
          mb="sm"
        />

        <TextInput
          label={t('pages.existingMortgageSummary.modal.email')}
          {...form.getInputProps('email')}
          required
          mb="sm"
        />

        <TextInput
          label={t('pages.existingMortgageSummary.modal.phoneNumber')}
          {...form.getInputProps('phoneNumber')}
          required
          mb="sm"
        />

        <TextInput
          label={t('pages.existingMortgageSummary.modal.fullName')}
          {...form.getInputProps('fullName')}
          required
          mb="sm"
        />
        <TextInput
          label={t('pages.existingMortgageSummary.modal.message')}
          {...form.getInputProps('message')}
          mb="sm"
        />
      </Stack>
    </Modal>
  );
};

export default SendDetailsModal;
